import {
  syncApplication,
  suspendApplication,
  resumeApplication,
  deleteApplication,
  forceDeleteApplication,
  retryCreateCRD,
  retryHelmValidation,
  retryApplicationSetup,
  startApplicationValidation,
} from '@/lib/api'
import { invalidateApplicationReadModels } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { useNavigate } from 'react-router-dom'

export function useApplicationActions(appId: string, projectId: string) {
  const navigate = useNavigate()

  const syncMutation = useMutationWithToast(syncApplication, {
    successTitle: 'Sync triggered',
    successMessage: 'Application sync has been initiated',
    errorTitle: 'Sync failed',
    invalidate: invalidateApplicationReadModels,
  })

  const retryCreateCRDMutation = useMutationWithToast(retryCreateCRD, {
    successTitle: 'ArgoCD application reconciled',
    successMessage: 'ArgoCD Application spec was patched with latest values.',
    errorTitle: 'ArgoCD reconcile failed',
    invalidate: invalidateApplicationReadModels,
  })

  const retryHelmValidationMutation = useMutationWithToast(retryHelmValidation, {
    successTitle: 'Retry initiated',
    successMessage: 'Helm validation is being retried...',
    errorTitle: 'Retry failed',
    invalidate: invalidateApplicationReadModels,
  })

  const retrySetupMutation = useMutationWithToast(retryApplicationSetup, {
    successTitle: 'Retry initiated',
    successMessage: 'Application setup is being retried...',
    errorTitle: 'Retry failed',
    invalidate: invalidateApplicationReadModels,
  })

  const startValidationMutation = useMutationWithToast(startApplicationValidation, {
    successTitle: 'Validation started',
    successMessage: 'Helm validation has been initiated.',
    errorTitle: 'Validation failed',
    invalidate: invalidateApplicationReadModels,
  })

  const suspendMutation = useMutationWithToast(suspendApplication, {
    successTitle: 'Application suspended',
    successMessage: 'The application has been suspended. Automated syncs are disabled and replicas set to 0.',
    errorTitle: 'Suspend failed',
    invalidate: invalidateApplicationReadModels,
  })

  const resumeMutation = useMutationWithToast(resumeApplication, {
    successTitle: 'Application resumed',
    successMessage: 'The application has been resumed. Syncs re-enabled and replicas restored.',
    errorTitle: 'Resume failed',
    invalidate: invalidateApplicationReadModels,
  })

  const deleteMutation = useMutationWithToast(deleteApplication, {
    successTitle: 'Application deleted',
    successMessage: 'The application is being deleted. Redirecting...',
    errorTitle: 'Delete failed',
    invalidate: invalidateApplicationReadModels,
    onSuccess: () => {
      // Navigate back to project page after a short delay
      setTimeout(() => {
        navigate(`/projects/${projectId}`, { replace: true })
      }, 1500)
    },
  })

  const forceDeleteMutation = useMutationWithToast(forceDeleteApplication, {
    successTitle: 'Application force deleted',
    successMessage: 'The application has been forcefully deleted. Redirecting...',
    errorTitle: 'Force delete failed',
    invalidate: invalidateApplicationReadModels,
    onSuccess: () => {
      setTimeout(() => {
        navigate(`/projects/${projectId}`, { replace: true })
      }, 1500)
    },
  })

  const handleSync = () => {
    syncMutation.mutate({ id: appId })
  }

  const handleRetryCRD = () => {
    retryCreateCRDMutation.mutate({ id: appId })
  }

  const handleReconcileCRD = () => {
    retryCreateCRDMutation.mutate({ id: appId })
  }

  const handleRetryHelm = () => {
    retryHelmValidationMutation.mutate({ id: appId })
  }

  const handleStartValidation = () => {
    startValidationMutation.mutate({ id: appId })
  }

  const handleRetrySetup = () => {
    retrySetupMutation.mutate({ id: appId })
  }

  const handleSuspend = () => {
    suspendMutation.mutate({ id: appId })
  }

  const handleResume = () => {
    resumeMutation.mutate({ id: appId })
  }

  const handleDelete = () => {
    deleteMutation.mutate({ id: appId })
  }

  const handleForceDelete = () => {
    forceDeleteMutation.mutate({ id: appId })
  }

  return {
    handleSync,
    handleSuspend,
    handleResume,
    handleDelete,
    handleForceDelete,
    handleRetryHelm,
    handleRetryCRD,
    handleReconcileCRD,
    handleRetrySetup,
    handleStartValidation,
    isLoading: {
      sync: syncMutation.isPending,
      suspend: suspendMutation.isPending,
      resume: resumeMutation.isPending,
      delete: deleteMutation.isPending,
      forceDelete: forceDeleteMutation.isPending,
      retryHelm: retryHelmValidationMutation.isPending,
      retryCRD: retryCreateCRDMutation.isPending,
      reconcileCRD: retryCreateCRDMutation.isPending,
      retrySetup: retrySetupMutation.isPending,
      startValidation: startValidationMutation.isPending,
    }
  }
}
